import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import InventoryLayout from './InventoryLayout';

const OrderView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadOrder = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/orders/${id}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente ordre');
      setOrder(data.order);
      setLines(data.lines || []);
    } catch (err) {
      setError(err.message || 'Kunne ikke hente ordre');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleMarkSent = async () => {
    try {
      const res = await fetch(`/api/orders/${id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'sent' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke markere sendt');
      loadOrder();
    } catch (err) {
      setError(err.message || 'Kunne ikke markere sendt');
    }
  };

  const total = lines.reduce((sum, line) => sum + line.qty * line.unit_price, 0);

  if (loading) {
    return (
      <InventoryLayout>
        <div className="p-4 text-gray-500">Indlæser...</div>
      </InventoryLayout>
    );
  }

  if (!order) {
    return (
      <InventoryLayout>
        <div className="p-4">
          <p className="text-gray-500">Ordre ikke fundet.</p>
          <button onClick={() => navigate('/indkoeb/ordrer')} className="text-falck-blue underline">
            Tilbage
          </button>
        </div>
      </InventoryLayout>
    );
  }

  return (
    <InventoryLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Ordre #{order.id}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">{order.partner_name}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="px-3 py-2 bg-gray-200 rounded-md">
            Print
          </button>
          <Link to={`/indkoeb/ordrer/${order.id}/modtag`} className="px-3 py-2 bg-falck-blue text-white rounded-md">
            Modtag
          </Link>
        </div>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Status: <span className="font-semibold text-gray-900 dark:text-gray-100">{order.status}</span>
          </div>
          {order.status === 'draft' && (
            <button onClick={handleMarkSent} className="text-sm text-falck-blue hover:underline">
              Markér sendt
            </button>
          )}
        </div>
        <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Reference: {order.reference || '—'}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-2 text-left">Vare</th>
                <th className="px-3 py-2 text-left">Varenummer</th>
                <th className="px-3 py-2 text-left">Antal</th>
                <th className="px-3 py-2 text-left">Pris</th>
                <th className="px-3 py-2 text-left">Modtaget</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {lines.map((line) => (
                <tr key={line.id}>
                  <td className="px-3 py-2 font-medium">{line.name}</td>
                  <td className="px-3 py-2">{line.sku || '—'}</td>
                  <td className="px-3 py-2">{line.qty}</td>
                  <td className="px-3 py-2">{Number(line.unit_price).toFixed(2)} kr</td>
                  <td className="px-3 py-2">{line.received_qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 text-right text-sm text-gray-600 dark:text-gray-300">
          Total: <span className="font-semibold text-gray-900 dark:text-gray-100">{total.toFixed(2)} kr</span>
        </div>
      </div>
    </InventoryLayout>
  );
};

export default OrderView;
